import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Button, FoodTrayTemplateLink, Select, Tabs, Theme, TitleSelect } from '../../components';
import { TabInfo } from '../../components/tabs/Tabs';
import { FoodTrayTemplate, GET_TEMPLATE_SORT_OPTIONS, SORT_ORDER, StoreField } from '../../models';
import { FoodTrayTemplateField } from '../../models';
import { RestoAdminLink } from '../../models/Routes';
import {
  foodTrayTemplateEditActions,
  foodTrayTemplatesActions,
  foodTrayTemplatesSelectors,
  storeActions,
  storeSelectors,
} from '../../redux';

import './RestoAdmin.scss';

const FoodTrayTemplates: FC<RouteComponentProps> = ({ location }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const stores = useSelector(storeSelectors.getStores);
  const store = useSelector(storeSelectors.getStore);
  const isLoading = useSelector(storeSelectors.isLoading);
  const templates = useSelector(foodTrayTemplatesSelectors.getFoodTrayTemplates);
  const isDuplicateLoading = useSelector(foodTrayTemplatesSelectors.isDuplicateLoading);
  const isTemplatesLoading = useSelector(foodTrayTemplatesSelectors.isLoading);

  const [activeDepartmentId, setActiveDepartmentId] = useQueryParam('department', StringParam);
  const [activeStoreId, setActiveStoreId] = useQueryParam('store', StringParam);
  const [sortField, setSortField] = useQueryParam('sort', StringParam);

  useEffect(() => {
    dispatch(
      storeActions.getStoresAndDepartments({
        offset: 0,
        limit: 50,
        sort: [StoreField.NAME + ',' + SORT_ORDER.ASC],
      }),
    );
  }, []);

  useEffect(() => {
    if (stores?.length && !activeStoreId) {
      setActiveStoreId(stores[0].id);
    }
  }, [stores, activeStoreId]);

  useEffect(() => {
    dispatch(foodTrayTemplatesActions.clearFoodTrayTemplate());
  }, []);

  useEffect(() => {
    dispatch(foodTrayTemplateEditActions.resetFoodTrayTemplate(activeDepartmentId));
  }, [activeDepartmentId]);

  useEffect(() => {
    if (activeStoreId) {
      dispatch(storeActions.getStore(activeStoreId));
    }
  }, [activeStoreId]);

  useEffect(() => {
    if (stores?.length && !activeStoreId && activeDepartmentId) {
      // This is a back link from the template edit page where we only know the department id
      // We can determine the store id by finding the uuid of the department
      const activeStore = stores?.find(store => store?.departments?.find(d => d?.id === activeDepartmentId));
      if (activeStore) {
        setActiveStoreId(activeStore?.id);
      }
    }
  }, [stores, activeStoreId, activeDepartmentId]);

  useEffect(() => {
    if (store?.departments?.length) {
      const department = store?.departments?.find(d => d.id === activeDepartmentId);
      if (!department) {
        // A new store was selected, so we should set the first department on the new store as the active department
        setActiveDepartmentId(store?.departments?.[0]?.id);
      }
    }
  }, [store]);

  useEffect(() => {
    if (!isDuplicateLoading && activeDepartmentId) {
      const sort = sortField || FoodTrayTemplateField.CREATED_AT;
      const sortOrder = GET_TEMPLATE_SORT_OPTIONS().find(sortOption => sortOption.value === sort)?.sortOrder || SORT_ORDER.DESC;
      dispatch(
        foodTrayTemplatesActions.getFoodTrayTemplates({
          sort: [sort + ',' + sortOrder],
          departmentId: activeDepartmentId,
        }),
      );
    }
  }, [activeDepartmentId, sortField, isDuplicateLoading]);

  const handleDepartmentChanged = (tab: TabInfo) => {
    setActiveDepartmentId(tab.value);
  };

  const renderTemplate = (template: FoodTrayTemplate | undefined, index: number) => {
    return (
      <FoodTrayTemplateLink
        template={template}
        link={{
          pathname: RestoAdminLink.FOOD_TRAY_TEMPLATES_EDIT.replace(':id', template?.id),
          state: { ...location },
        }}
        onDuplicateTemplate={() => !!template && dispatch(foodTrayTemplatesActions.duplicateFoodTrayTemplate(template?.id))}
        isDuplicateLoading={isDuplicateLoading}
        key={'food-tray-template-link-' + template?.id + '-' + index}
      />
    );
  };

  return (
    <div className='resto-admin content extra-margin'>
      <div className='top-buttons'>
        <Button theme={Theme.SECONDARY} icon='Add' link={RestoAdminLink.FOOD_TRAY_TEMPLATES_CREATE}>
          {t('lblAddTemplate')}
        </Button>
      </div>
      <section>
        <h1 className='title'>{t('titleFoodLineTemplates')}</h1>
        <TitleSelect
          options={stores.map(s => ({ value: s.id, label: s.name }))}
          value={activeStoreId}
          isLoading={isLoading}
          onChange={setActiveStoreId}
        />
        <Tabs
          tabs={store?.departments?.map(dep => ({
            label: dep.departmentTemplate.name,
            value: dep.id,
          }))}
          value={activeDepartmentId || store?.departments?.[0]?.id}
          onChange={handleDepartmentChanged}
          isLoading={isLoading}
        />
        <div className='sort-select'>
          <Select label='lblSortBy' value={sortField} onChange={setSortField} options={GET_TEMPLATE_SORT_OPTIONS()} />
        </div>
        <div className='templates'>{(isTemplatesLoading ? [undefined, undefined] : templates).map(renderTemplate)}</div>
      </section>
    </div>
  );
};

export default withRouter(FoodTrayTemplates);
